import { useEffect, useState } from 'react';
import { markVisited, readLastVisited, type VisitedSurface } from './last-visited';
import { newSince } from './summary-freshness';

// The React half of the `new` badge: one hook that STAMPS a surface as visited, and one that reads
// the mark back for the hub tiles.

/**
 * Mark the surface visited — call it from the screen the reader actually opened (`/routines`,
 * `/tasks`, `/files`), not from the tile that links to it.
 */
export function useMarkVisited(surface: VisitedSurface): void {
  useEffect(() => {
    markVisited(surface);
  }, [surface]);
}

/**
 * The moment "new" starts from for this surface, **captured once at mount**, or `null` before the
 * first effect has run.
 *
 * Reading the stamp live would make the badge disappear underneath the reader: opening `/tasks`
 * writes a fresh stamp, and coming straight back to the hub would re-read it and drop the badge to
 * zero mid-render — which looks like a glitch rather than like "you've seen these now". Capturing at
 * mount means the badge holds still for as long as the reader is on the hub, and is recomputed the
 * next time they arrive.
 *
 * It starts `null` rather than computing during render for a second reason: the server has no
 * localStorage, so a value computed at render time would differ between the server's HTML and the
 * client's first paint and trip hydration. `null` renders no badge on both, and the badge appears on
 * the first client effect.
 */
export function useNewSince(surface: VisitedSurface): number | null {
  const [since, setSince] = useState<number | null>(null);
  useEffect(() => {
    setSince(newSince(readLastVisited(surface), Date.now()));
  }, [surface]);
  return since;
}
